import type { VeyopRuntime } from '@core/veyopRuntime';

/** Widened to string so addon-registered views (see Veyop.views) carry masks too. */
export type ViewName = string;

export type MaskPrintStyle = 'transparent' | 'cutline';

export interface MaskImage {
  url: string;
  top: number;
  left: number;
  width: number;
  height: number;
  z?: number;
  /** When true, this mask is its own layer in the production SVG. */
  includeInPrint?: boolean;
  /** Transparent image keeps alpha. Cut line is a stroke in the SVG only. */
  printStyle?: MaskPrintStyle;
}

export type MaskMap = Record<ViewName, MaskImage[]>;

export interface CatalogEditContext {
  productId: number;
  activeView: ViewName;
  canvasWidth: number;
  canvasHeight: number;
  design: Record<string, unknown>;
  updateDesign: (key: string, value: unknown) => void;
  markDirty?: () => void;
}

export interface DesignerOverlayContext {
  viewName: ViewName;
  config: Record<string, unknown>;
}

const CORE_VIEW_NAMES: ViewName[] = ['front', 'back', 'left', 'right'];

/** Registered product views via the shared host registry; core 4 as fallback. */
export function maskViewNames(): ViewName[] {
  try {
    const veyop = typeof window !== 'undefined' ? (window as Window & { Veyop?: VeyopRuntime }).Veyop : undefined;
    const registered = veyop?.views?.getAll?.() ?? [];
    if (registered.length > 0) return registered.map((view) => view.id);
  } catch { /* registry unavailable in isolated test contexts */ }
  return CORE_VIEW_NAMES;
}

/** @deprecated Use maskViewNames(); kept for older bundles. */
export const VIEW_NAMES: ViewName[] = CORE_VIEW_NAMES;
export const MASK_KEY = 'mask_images';
export const EDGE_MIN = -450;
export const EDGE_MAX = 450;
export const SIZE_MIN = 0.25;
export const SIZE_MAX = 500;

export function emptyMaskMap(): MaskMap {
  return Object.fromEntries(maskViewNames().map((view) => [view, []])) as MaskMap;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function clampCoord(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(EDGE_MIN, Math.min(EDGE_MAX, n));
}

export function clampSize(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 100;
  return Math.max(SIZE_MIN, Math.min(SIZE_MAX, n));
}

export function normalizePrintStyle(value: unknown): MaskPrintStyle {
  return value === 'cutline' ? 'cutline' : 'transparent';
}

export function normalizeMask(raw: unknown, index: number): MaskImage | null {
  if (typeof raw === 'string' && raw.trim()) {
    return { url: raw.trim(), top: 0, left: 0, width: 100, height: 100, z: 10000 + index, includeInPrint: false, printStyle: 'transparent' };
  }
  if (!isRecord(raw) || typeof raw.url !== 'string' || raw.url.trim() === '') return null;
  return {
    url: raw.url.trim(),
    top: clampCoord(raw.top),
    left: clampCoord(raw.left),
    width: clampSize(raw.width),
    height: clampSize(raw.height),
    z: Number.isFinite(Number(raw.z)) ? Number(raw.z) : 10000 + index,
    includeInPrint: raw.includeInPrint === true,
    printStyle: normalizePrintStyle(raw.printStyle),
  };
}

export function normalizeMaskMap(raw: unknown): MaskMap {
  let data = raw;
  if (typeof data === 'string' && data.trim()) {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  const source = isRecord(data) ? data : {};
  const out = emptyMaskMap();
  for (const view of [...new Set([...maskViewNames(), ...Object.keys(source)])]) {
    const list = source[view];
    out[view] = Array.isArray(list)
      ? list.map((item, index) => normalizeMask(item, index)).filter((item): item is MaskImage => item !== null)
      : [];
  }
  return out;
}

export function getMasks(design: Record<string, unknown> | undefined): MaskMap {
  return normalizeMaskMap(design?.[MASK_KEY]);
}

export function getDesignerMasks(config: Record<string, unknown> | undefined): MaskMap {
  if (!config) return emptyMaskMap();
  return normalizeMaskMap(config.maskImages ?? config[MASK_KEY]);
}

export function withViewMasks(source: unknown, view: ViewName, masks: MaskImage[]): MaskMap {
  const next = normalizeMaskMap(source);
  next[view] = masks.map((mask, index) => ({ ...mask, z: 10000 + index }));
  return next;
}

export function heightForWidthPct(widthPct: number, imageAspect: number, canvasWidth: number, canvasHeight: number): number {
  if (!Number.isFinite(imageAspect) || imageAspect <= 0 || canvasHeight <= 0) return widthPct;
  return (widthPct * Math.max(1, canvasWidth)) / (imageAspect * Math.max(1, canvasHeight));
}

export function widthForHeightPct(heightPct: number, imageAspect: number, canvasWidth: number, canvasHeight: number): number {
  if (!Number.isFinite(imageAspect) || imageAspect <= 0 || canvasWidth <= 0) return heightPct;
  return (heightPct * imageAspect * Math.max(1, canvasHeight)) / Math.max(1, canvasWidth);
}

export function renderedAspect(mask: Pick<MaskImage, 'width' | 'height'>, canvasWidth: number, canvasHeight: number): number {
  const renderedWidth = Math.max(SIZE_MIN, mask.width) * Math.max(1, canvasWidth);
  const renderedHeight = Math.max(SIZE_MIN, mask.height) * Math.max(1, canvasHeight);
  const aspect = renderedWidth / renderedHeight;
  return Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
}

export function defaultMask(
  url: string,
  index: number,
  imageAspect = 1,
  canvasWidth = 1,
  canvasHeight = 1,
): MaskImage {
  const width = 100;
  const height = clampSize(heightForWidthPct(width, imageAspect, canvasWidth, canvasHeight));
  return { url, top: 0, left: 0, width, height, z: 10000 + index, includeInPrint: false, printStyle: 'transparent' };
}
